/**
 * The mark of a buy-list purchase order: "Just in time" (a batch's shopping
 * list, BLU-1268) or "Order and wait" (blanks bought ahead for orders that
 * wait, BLU-1506). A KIND, not a state, so it wears the brand tint
 * (`bg-primary/10 text-primary-strong`, DESIGN.md section 1) rather than a
 * status colour, with a glyph per kind so colour is never the only signal.
 *
 * `bar` sits on the action bar's h-8 baseline beside the status badge;
 * `inline` sits after a PO number in a table row or a card.
 */
import { Hourglass, ShoppingBasket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BuyListPoKind } from '@threadheads/shared';
import { cn } from '@/lib/utils';

const KIND = {
  just_in_time: { icon: ShoppingBasket, label: 'kind.justInTime' },
  order_and_wait: { icon: Hourglass, label: 'kind.orderAndWait' },
} as const satisfies Record<BuyListPoKind, { icon: typeof ShoppingBasket; label: string }>;

export interface PoKindBadgeProps {
  kind: BuyListPoKind;
  size?: 'bar' | 'inline';
  className?: string;
}

export function PoKindBadge({ kind, size = 'inline', className }: PoKindBadgeProps) {
  const { t } = useTranslation('orders');
  const { icon: Icon, label } = KIND[kind];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary/10 font-medium text-primary-strong ring-1 ring-primary/20',
        size === 'bar' ? 'h-8 px-3 text-[11px]' : 'px-1.5 py-0.5 text-[10px]',
        className,
      )}
    >
      <Icon className={size === 'bar' ? 'h-3.5 w-3.5' : 'h-3 w-3'} aria-hidden="true" />
      {t(label)}
    </span>
  );
}
